/**
 * businessInsightsFinanceData.ts — Tarjetas demo de finanzas.
 *
 * QUÉ HACE: declara las tarjetas 23–27 del explorador de Análisis del negocio
 *   (flujo de caja, costos fijos vs variables, utilidad neta diaria, descuentos
 *   y cuentas por cobrar) con datos DEMO deterministas para la pestaña de
 *   pruebas local.
 *
 * QUÉ INVOCA: helpers de `businessInsights.shared.ts`.
 * QUÉ EXPORTA: BUSINESS_INSIGHTS_FINANCE (readonly BusinessInsightDataset[]).
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 */
import { Scale, TrendingUp, Wallet } from "lucide-react";
import type { BusinessInsightDataset } from "../types/businessInsights.types";
import {
  INSIGHT_COLORS,
  INSIGHT_VIEW_PRESETS,
  insightCandleRow,
  insightRow,
} from "./businessInsights.shared";

const MONTHS = ["Abr", "May", "Jun", "Jul", "Ago", "Sep"] as const;

const FLUJO_SEMANA = [
  { dia: "Lun", entradas: 4200000, salidas: 2600000 },
  { dia: "Mar", entradas: 3800000, salidas: 3100000 },
  { dia: "Mié", entradas: 3400000, salidas: 2200000 },
  { dia: "Jue", entradas: 5100000, salidas: 3000000 },
  { dia: "Vie", entradas: 4600000, salidas: 5400000 },
  { dia: "Sáb", entradas: 6200000, salidas: 2900000 },
  { dia: "Dom", entradas: 4900000, salidas: 2400000 },
] as const;

export const BUSINESS_INSIGHTS_FINANCE: readonly BusinessInsightDataset[] = [
  {
    id: "flujo-caja",
    title: "Flujo de caja diario",
    subtitle: "Entradas, salidas y neto — saldo actual $64.2M",
    icon: Wallet,
    group: "finanzas",
    valueKind: "currency",
    nameLabel: "Día",
    series: [
      { key: "entradas", label: "Entradas", color: INSIGHT_COLORS.exito },
      { key: "salidas", label: "Salidas", color: INSIGHT_COLORS.gastos },
      { key: "neto", label: "Neto", color: INSIGHT_COLORS.caja },
    ],
    rows: FLUJO_SEMANA.map(({ dia, entradas, salidas }) =>
      insightRow(dia, { entradas, salidas, neto: entradas - salidas }),
    ),
    views: ["combinada", ...INSIGHT_VIEW_PRESETS.temporalSinVelas, "radar"],
    defaultView: "combinada",
    composed: { lineKeys: ["neto"] },
    badge: {
      mode: "total",
      label: "Neto semanal",
      tone: "green",
      seriesKey: "neto",
    },
    showTotal: false,
  },
  {
    id: "costos-fijos-variables",
    title: "Costos fijos vs variables",
    subtitle: "Estructura de costos — últimos 6 meses",
    icon: Scale,
    group: "finanzas",
    valueKind: "currency",
    nameLabel: "Mes",
    series: [
      { key: "fijos", label: "Fijos", color: INSIGHT_COLORS.naranja },
      { key: "variables", label: "Variables", color: INSIGHT_COLORS.info },
    ],
    rows: MONTHS.map((month, index) =>
      insightRow(month, {
        fijos: [46000000, 47000000, 48000000, 48000000, 49000000, 50000000][
          index
        ],
        variables: [22000000, 24000000, 25000000, 26000000, 27000000, 28000000][
          index
        ],
      }),
    ),
    views: ["apiladas", ...INSIGHT_VIEW_PRESETS.temporal, "radar"],
    defaultView: "apiladas",
    badge: { mode: "total", label: "Costo 6 meses", tone: "amber" },
  },
  {
    id: "utilidad-neta-diaria",
    title: "Utilidad neta diaria",
    subtitle: "Ganancia después de costos — últimos 7 días",
    icon: TrendingUp,
    group: "finanzas",
    valueKind: "currency",
    nameLabel: "Día",
    series: [
      { key: "utilidad", label: "Utilidad", color: INSIGHT_COLORS.exito },
    ],
    rows: [
      insightCandleRow("Lun", { utilidad: 2100000 }, "utilidad"),
      insightCandleRow("Mar", { utilidad: 3400000 }, "utilidad"),
      insightCandleRow("Mié", { utilidad: 1800000 }, "utilidad"),
      insightCandleRow("Jue", { utilidad: 4200000 }, "utilidad"),
      insightCandleRow("Vie", { utilidad: -800000 }, "utilidad"),
      insightCandleRow("Sáb", { utilidad: 6100000 }, "utilidad"),
      insightCandleRow("Dom", { utilidad: 3900000 }, "utilidad"),
    ],
    views: [...INSIGHT_VIEW_PRESETS.temporal, "radar"],
    colorBySign: true,
    badge: { mode: "max", label: "Mejor día", tone: "green" },
  },
];
